import dayjs, { Dayjs } from "dayjs";
import { FormikErrors, useFormik } from "formik";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";

import { IApiError, IApiErrorResponse } from "../../@types/ApiError";
import {
  ITmpOnboardingCompleteRequest,
  ITmpOnboardingPrefill,
} from "../../@types/TmpOnboarding";
import { CircleTick } from "../../assets/icons/CircleTick";
import { CircleX } from "../../assets/icons/CircleX";
import { Button } from "../../components/Button";
import { DateTimePickerInput } from "../../components/DateTimePicker";
import { TextField } from "../../components/TextField";
import {
  parseTmpRegion,
  tmpOnboardingLinkStore,
  tmpOnboardingService,
  TTmpRegion,
} from "../../services/tmpOnboardingService";
import { colors } from "../../styles/colors";
import { preLoginLogo } from "../../styles/preLoginBranding";
import { ReturnCountdown } from "./ReturnCountdown";
import {
  Card,
  FieldError,
  FieldGroup,
  FieldLabel,
  Form,
  FormError,
  IconContainer,
  InfoText,
  Logo,
  MainContainer,
  Message,
  RadioOption,
  RadioRow,
  SectionTitle,
  Spinner,
  submitButtonStyles,
  SummaryBlock,
  Title,
} from "./TmpOnboardingPage.styles";

type TPageState =
  | { kind: "loading" }
  | { kind: "form"; prefill: ITmpOnboardingPrefill }
  | { kind: "done" }
  | { kind: "success"; reactivated: boolean }
  | { kind: "expired" }
  | { kind: "error"; message: string };

interface ITmpOnboardingFormValues {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  /** `YYYY-MM-DD` as the shared date picker reads and writes it; "" for unset. */
  dateOfBirth: string;
  gender: "" | "male" | "female";
}

const EXPIRED_MESSAGE =
  "This link is invalid or has expired. Please ask your hospital administrator for a new one.";
const GENERIC_ERROR_MESSAGE =
  "Sorry, something went wrong. Please try again in a few minutes, or ask your hospital administrator for a new link.";
const SUCCESS_MESSAGE =
  "Thank you. The patient will be enrolled in patientMpower within a few minutes and will receive an invitation email.";
const REACTIVATION_SUCCESS_MESSAGE =
  "Thank you. The existing patientMpower patient will be reactivated within a few minutes and this telemonitoring order linked to them.";

/** What the date picker stores — also the ISO date the API takes. */
const DATE_VALUE = "YYYY-MM-DD";

/** The API caps each name at 30 characters. */
export const NAME_MAX_LENGTH = 30;

function isErrorBody(value: unknown): value is IApiErrorResponse {
  return (
    !!value &&
    typeof value === "object" &&
    Array.isArray((value as { errors?: unknown }).errors)
  );
}

/**
 * Maps the API's `propertyName` onto the form field it belongs to. The API
 * names DTO properties (PascalCase); anything unmatched becomes a form-level
 * error rather than being silently dropped.
 */
const PROPERTY_TO_FIELD: Record<string, keyof ITmpOnboardingFormValues> = {
  firstname: "firstName",
  lastname: "lastName",
  email: "email",
  dateofbirth: "dateOfBirth",
  birthdate: "dateOfBirth",
  gender: "gender",
};

export function splitValidationErrors(errors: IApiError[]): {
  fieldErrors: Partial<Record<keyof ITmpOnboardingFormValues, string>>;
  formErrors: string[];
} {
  const fieldErrors: Partial<Record<keyof ITmpOnboardingFormValues, string>> =
    {};
  const formErrors: string[] = [];
  errors.forEach((error) => {
    const field = error.propertyName
      ? PROPERTY_TO_FIELD[error.propertyName.toLowerCase()]
      : undefined;
    if (field) {
      fieldErrors[field] = error.message;
    } else if (error.message) {
      formErrors.push(error.message);
    }
  });
  return { fieldErrors, formErrors };
}

function formatKnownDate(iso: string | null): string | null {
  if (!iso) return null;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : iso;
}

function capitalise(value: string | null): string | null {
  if (!value) return null;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The required fields come from `missingFields`, the optional demographics
 * from `optionalFields`. Optional inputs are validated for format/range only
 * once something has been entered — an empty one is simply not sent.
 */
function buildValidationSchema(missing: Set<string>, optional: Set<string>) {
  const shape: Record<string, yup.AnySchema> = {};

  if (missing.has("firstName")) {
    shape.firstName = yup
      .string()
      .trim()
      .required("Please enter the patient's first name.")
      .max(
        NAME_MAX_LENGTH,
        `First name must be at most ${NAME_MAX_LENGTH} characters.`
      );
  }

  if (missing.has("lastName")) {
    shape.lastName = yup
      .string()
      .trim()
      .required("Please enter the patient's last name.")
      .max(
        NAME_MAX_LENGTH,
        `Last name must be at most ${NAME_MAX_LENGTH} characters.`
      );
  }

  if (missing.has("email")) {
    shape.email = yup
      .string()
      .trim()
      .required("Please enter the patient's email address.")
      .email("Please enter a valid email address.");
    shape.confirmEmail = yup
      .string()
      .trim()
      .required("Please confirm the email address.")
      .oneOf([yup.ref("email")], "The email addresses do not match.");
  }

  if (optional.has("birthDate")) {
    // The picker only commits a real date, so format is its concern; the
    // rule left to check is that a birth date is not in the future.
    shape.dateOfBirth = yup
      .string()
      .test(
        "not-in-future",
        "The date of birth cannot be in the future.",
        (value) => !value || !dayjs(value, DATE_VALUE).isAfter(dayjs(), "day")
      );
  }

  if (optional.has("gender")) {
    shape.gender = yup
      .string()
      .oneOf(["", "male", "female"], "Please select the patient's sex.");
  }

  return yup.object().shape(shape);
}

interface ISummaryRow {
  label: string;
  value: string | null;
}

function PrefillSummary({ prefill }: { prefill: ITmpOnboardingPrefill }) {
  const rows: ISummaryRow[] = [
    { label: "First name", value: prefill.firstName },
    { label: "Last name", value: prefill.lastName },
    { label: "Hospital patient ID", value: prefill.externalPatientId },
    { label: "Date of birth", value: formatKnownDate(prefill.dateOfBirth) },
    { label: "Sex", value: capitalise(prefill.gender) },
    { label: "Email", value: prefill.email },
  ];

  return (
    <SummaryBlock data-testid="tmp-prefill-summary">
      {rows
        .filter((row) => row.value)
        .map((row) => (
          <Fragment key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </Fragment>
        ))}
    </SummaryBlock>
  );
}

interface ICompletionFormProps {
  code: string;
  region: TTmpRegion;
  prefill: ITmpOnboardingPrefill;
  onSuccess: () => void;
  onExpired: () => void;
}

function CompletionForm({
  code,
  region,
  prefill,
  onSuccess,
  onExpired,
}: ICompletionFormProps) {
  const missing = new Set<string>(prefill.missingFields);
  const optional = new Set<string>(prefill.optionalFields ?? []);
  const asksFirstName = missing.has("firstName");
  const asksLastName = missing.has("lastName");
  const asksEmail = missing.has("email");
  const asksBirthDate = optional.has("birthDate");
  const asksGender = optional.has("gender");
  const asksOptional = asksBirthDate || asksGender;
  const isReactivation = prefill.requiresReactivation;
  // Nothing required means the provisioner already has what it needs; the
  // optional demographics alone are not worth stopping the clinician for.
  // A reactivation always needs the clinician's explicit confirmation.
  const asksAnything = prefill.missingFields.length > 0 || isReactivation;

  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const formik = useFormik<ITmpOnboardingFormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      dateOfBirth: "",
      gender: "",
    },
    validationSchema: buildValidationSchema(missing, optional),
    onSubmit: async (values) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      setFormErrors([]);

      const request: ITmpOnboardingCompleteRequest = {};
      if (asksFirstName) request.firstName = values.firstName.trim();
      if (asksLastName) request.lastName = values.lastName.trim();
      if (asksEmail) request.email = values.email.trim();
      if (asksBirthDate && values.dateOfBirth) {
        request.dateOfBirth = values.dateOfBirth;
      }
      if (asksGender && values.gender) request.gender = values.gender;

      try {
        const response = await tmpOnboardingService.complete(
          code,
          region,
          request
        );
        const status = response?.status ?? 0;

        if (status >= 200 && status < 300) {
          onSuccess();
          return;
        }
        if (status === 404) {
          onExpired();
          return;
        }
        if (status === 400 && isErrorBody(response.data)) {
          const { fieldErrors, formErrors: unmapped } = splitValidationErrors(
            response.data.errors
          );
          // Yup reports the date-of-birth test at the object level, as a
          // string, which Formik accepts at runtime but does not type.
          formik.setErrors(
            fieldErrors as unknown as FormikErrors<ITmpOnboardingFormValues>
          );
          if (unmapped.length > 0) {
            setFormErrors(unmapped);
          } else if (Object.keys(fieldErrors).length === 0) {
            setFormErrors([
              "The details could not be saved. Please check them and try again.",
            ]);
          }
          return;
        }
        setFormErrors([GENERIC_ERROR_MESSAGE]);
      } catch (error) {
        console.error(error);
        setFormErrors([GENERIC_ERROR_MESSAGE]);
      } finally {
        setIsSubmitting(false);
        isSubmittingRef.current = false;
      }
    },
  });

  const shownError = (field: keyof ITmpOnboardingFormValues): string => {
    const touched = formik.touched[field];
    const error = formik.errors[field];
    if (!touched && !formik.submitCount) return "";
    return typeof error === "string" ? error : "";
  };

  const dateError =
    formik.touched.dateOfBirth || formik.submitCount > 0
      ? formik.errors.dateOfBirth ?? ""
      : "";

  const dateOfBirthValue: Dayjs | null = formik.values.dateOfBirth
    ? dayjs(formik.values.dateOfBirth, DATE_VALUE)
    : null;

  return (
    <>
      {isReactivation ? (
        <>
          <Title>Reactivate the patient</Title>
          <Message data-testid="tmp-reactivation-notice">
            A patient with MRN {prefill.externalPatientId} already exists in
            patientMpower but is archived. Review the details below: submitting
            will reactivate this existing patient and link the new Transmural
            Platform order to them. No new patient is created. To change the
            details shown, update the patient in the patientMpower portal after
            the reactivation.
          </Message>
        </>
      ) : (
        <>
          <Title>Complete the patient&apos;s details</Title>
          <Message>
            The Transmural Platform order for this patient is missing some
            details that patientMpower needs before the patient can be enrolled.
            Please fill in the fields below.
          </Message>
        </>
      )}

      <PrefillSummary prefill={prefill} />

      {!asksAnything ? (
        <Message data-testid="tmp-nothing-missing">
          Nothing further is needed from you. The patient will be enrolled
          automatically.
        </Message>
      ) : (
        <Form onSubmit={formik.handleSubmit} noValidate>
          {asksFirstName && (
            <TextField
              type="text"
              name="firstName"
              label="First name *"
              maxLength={NAME_MAX_LENGTH}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={shownError("firstName")}
            />
          )}

          {asksLastName && (
            <TextField
              type="text"
              name="lastName"
              label="Last name *"
              maxLength={NAME_MAX_LENGTH}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={shownError("lastName")}
            />
          )}

          {asksEmail && (
            <>
              <TextField
                type="email"
                name="email"
                label="Patient's email address *"
                placeholder="name@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={shownError("email")}
              />
              <TextField
                type="email"
                name="confirmEmail"
                label="Confirm email address *"
                placeholder="name@example.com"
                value={formik.values.confirmEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={shownError("confirmEmail")}
              />
            </>
          )}

          {asksOptional && (
            <FieldGroup data-testid="tmp-optional-details">
              <SectionTitle>Optional details</SectionTitle>
              <Message>
                These are not required for enrolment. Fill in what you know;
                anything left empty is simply not recorded.
              </Message>
            </FieldGroup>
          )}

          {asksBirthDate && (
            <FieldGroup>
              <FieldLabel htmlFor="tmp-date-of-birth">Date of birth</FieldLabel>
              <DateTimePickerInput
                id="tmp-date-of-birth"
                width="100%"
                // Date only: the picker defaults to date + time, whose display
                // format would reject a typed plain date.
                showTime={false}
                format="DD/MM/YYYY"
                placeholder="Select a date"
                value={dateOfBirthValue?.isValid() ? dateOfBirthValue : null}
                // Clearing hands back null despite the signature, hence the guard.
                onChange={(next: Dayjs | null) => {
                  formik.setFieldTouched("dateOfBirth", true, false);
                  formik.setFieldValue(
                    "dateOfBirth",
                    next?.isValid() ? next.format(DATE_VALUE) : ""
                  );
                }}
              />
              {dateError && <FieldError role="alert">{dateError}</FieldError>}
            </FieldGroup>
          )}

          {asksGender && (
            <FieldGroup>
              <FieldLabel id="tmp-gender-label">Sex</FieldLabel>
              <RadioRow role="radiogroup" aria-labelledby="tmp-gender-label">
                <RadioOption>
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={formik.values.gender === "male"}
                    onChange={() => formik.setFieldValue("gender", "male")}
                    onBlur={formik.handleBlur}
                  />
                  Male
                </RadioOption>
                <RadioOption>
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={formik.values.gender === "female"}
                    onChange={() => formik.setFieldValue("gender", "female")}
                    onBlur={formik.handleBlur}
                  />
                  Female
                </RadioOption>
              </RadioRow>
              {shownError("gender") && (
                <FieldError role="alert">{shownError("gender")}</FieldError>
              )}
            </FieldGroup>
          )}

          {formErrors.length > 0 && (
            <FormError role="alert" data-testid="tmp-form-error">
              {formErrors.join("\n")}
            </FormError>
          )}

          <Button
            type="submit"
            label={isReactivation ? "Reactivate patient" : "Submit details"}
            isLoading={isSubmitting}
            className={submitButtonStyles()}
          />
        </Form>
      )}
    </>
  );
}

/**
 * Anonymous completion page for Transmural Platform orders (EN-5203):
 * `/tmp/complete?code=<one-time code>&region=<EU|US>`. Lives outside
 * DashboardLayout — the hospital clinician has no portal account. The code is
 * the only credential, so it and the region are moved from the address bar
 * into state on first render and kept in this tab's sessionStorage so a
 * reload still works; the code is consumed by the PUT (and the stored copy
 * cleared), the region tells the API which database holds it.
 */
export function TmpOnboardingPage() {
  document.title = "Complete patient details - patientMpower";

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(null);
  const [region, setRegion] = useState<TTmpRegion | null>(null);
  const [state, setState] = useState<TPageState>({ kind: "loading" });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Only `code` and `region` matter; TMP may append others (e.g. a
    // `tmp-token` when the link is opened from the EHR), which are ignored.
    // A link without a valid region is as unusable as one without a code:
    // there is no database to look the code up in, so no request is made.
    // A reload finds the address bar already cleaned: fall back to the copy
    // kept for this tab. A fresh link in the URL always wins over it.
    const fromUrl = {
      code: searchParams.get("code"),
      region: parseTmpRegion(searchParams.get("region")),
    };
    let link: { code: string; region: TTmpRegion } | null = null;
    if (fromUrl.code && fromUrl.region) {
      link = { code: fromUrl.code, region: fromUrl.region };
    } else if (!fromUrl.code) {
      link = tmpOnboardingLinkStore.read();
    }
    // Clean the address bar first, whatever happens next: an unusable link can
    // still carry a live code, which must not stay in the URL or the history.
    if (searchParams.toString()) {
      navigate({ pathname: "/tmp/complete", search: "" }, { replace: true });
    }
    if (!link) {
      setState({ kind: "expired" });
      return;
    }

    const { code: codeParam, region: regionParam } = link;
    tmpOnboardingLinkStore.save(link);
    setCode(codeParam);
    setRegion(regionParam);

    tmpOnboardingService
      .getPrefill(codeParam, regionParam)
      .then((response) => {
        const status = response?.status ?? 0;
        if (status === 404) {
          tmpOnboardingLinkStore.clear();
          setState({ kind: "expired" });
          return;
        }
        if (status < 200 || status >= 300 || isErrorBody(response.data)) {
          const message = isErrorBody(response.data)
            ? response.data.errors
                .map((e) => e.message)
                .filter(Boolean)
                .join(" ")
            : "";
          setState({
            kind: "error",
            message: message || GENERIC_ERROR_MESSAGE,
          });
          return;
        }
        const prefill = response.data as ITmpOnboardingPrefill;
        if (!prefill.canComplete) {
          tmpOnboardingLinkStore.clear();
          setState({ kind: "done" });
          return;
        }
        setState({ kind: "form", prefill });
      })
      .catch((error) => {
        console.error(error);
        setState({ kind: "error", message: GENERIC_ERROR_MESSAGE });
      });
  }, [navigate, searchParams]);

  let content: ReactNode;
  let centered = false;

  switch (state.kind) {
    case "loading":
      centered = true;
      content = (
        <>
          <IconContainer status="loading">
            <Spinner />
          </IconContainer>
          <Title>Loading the order...</Title>
          <Message>Please wait a moment.</Message>
        </>
      );
      break;
    case "form":
      content = (
        <CompletionForm
          code={code!}
          region={region!}
          prefill={state.prefill}
          onSuccess={() => {
            tmpOnboardingLinkStore.clear();
            setState({
              kind: "success",
              reactivated: state.prefill.requiresReactivation,
            });
          }}
          onExpired={() => {
            tmpOnboardingLinkStore.clear();
            setState({ kind: "expired" });
          }}
        />
      );
      break;
    case "success":
      centered = true;
      content = (
        <>
          <IconContainer status="success">
            <CircleTick width={44} height={44} color={colors.success} />
          </IconContainer>
          <Title>
            {state.reactivated ? "Reactivation confirmed" : "Details saved"}
          </Title>
          <Message data-testid="tmp-success">
            {state.reactivated ? REACTIVATION_SUCCESS_MESSAGE : SUCCESS_MESSAGE}
          </Message>
          <ReturnCountdown />
        </>
      );
      break;
    case "done":
      centered = true;
      content = (
        <>
          <IconContainer status="success">
            <CircleTick width={44} height={44} color={colors.success} />
          </IconContainer>
          <Title>This order has already been completed</Title>
          <Message data-testid="tmp-already-done">
            There is nothing more to do for this patient. If you believe this is
            a mistake, please contact your hospital administrator.
          </Message>
          <InfoText>You can close this window.</InfoText>
        </>
      );
      break;
    case "expired":
      centered = true;
      content = (
        <>
          <IconContainer status="error">
            <CircleX width={44} height={44} color={colors.error} />
          </IconContainer>
          <Title>Link invalid or expired</Title>
          <Message data-testid="tmp-expired">{EXPIRED_MESSAGE}</Message>
        </>
      );
      break;
    default:
      centered = true;
      content = (
        <>
          <IconContainer status="error">
            <CircleX width={44} height={44} color={colors.error} />
          </IconContainer>
          <Title>Something went wrong</Title>
          <Message data-testid="tmp-error">{state.message}</Message>
        </>
      );
  }

  return (
    <MainContainer>
      <Card centered={centered}>
        <Logo src={preLoginLogo.src} alt={preLoginLogo.alt} />
        {content}
      </Card>
    </MainContainer>
  );
}
